/**
 * THE DRAFT — what is in a composer's box, and everything that follows from it.
 *
 * The composer exists twice: the opening one (`composer.ts`) and the dock under an open thread
 * (`chat.ts`). Both must treat a draft the same way — its @ mentions, the list that completes a
 * command or a mention, the keys that list takes, what a typed command does, and where the draft is
 * kept. So it is written once, here, as a controller each of them holds, and the two cannot drift.
 * What stays in each view is what differs between them: the markup around the box, what a command
 * can do there, and what sending means.
 *
 * The rules themselves are pure functions of text (`commands.ts`, `mentions.ts`). This is the state
 * that applies them, and the handlers a view binds to its textarea.
 */
import { css, html, nothing } from 'lit';
import { t } from '../i18n';
import { imeComposing } from './composer-text';
import { findCommand, parseCommand } from './commands';
import { applyMention, composerSuggestions, mentionsFor, rebaseMentions, renderMentionBackdrop, serializeDraft, } from './mentions';
import { TEXT_MODELS } from './model-catalog';
import './panel-commands';
import { readDraft, readDraftMentions, saveDraft } from './session-memory';
import { specialistLabel } from './specialists';
import { activeRowId, renderSuggestList, suggestStyles } from './suggest-list';
import { EFFORTS } from './tools-menu';
/** The list's ID. One list per view, inside that view's own shadow root. */
const LIST_ID = 'cmd-suggest';
/** The keys that move the caret without changing the text — see `onCaret`. */
const CARET_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'Home', 'End']);
/**
 * WHAT THE EMPTY BOX SAYS — and the suggested next reply is said HERE, as the box's own grey text.
 *
 * The placeholder already behaves exactly as an offered line should: shown while the box is empty,
 * gone at the first keystroke, back when the box is cleared, read out by a screen reader, and left
 * alone by an IME. Drawing the line elsewhere (under the answer, or as an overlay on the box) would
 * have to rebuild each of those by hand. The text is the line alone, no "Tab" hint, so what is grey
 * is exactly what Tab puts in the box.
 */
export function draftPlaceholder(busy, line, steering = false) {
    if (busy)
        return t('ai.thinking');
    // What is typed here goes to a working agent, which answers at its next step and not at once —
    // said before the person sends, so the wait reads as the agent working rather than as no reply.
    if (steering)
        return t('ai.steerPlaceholder');
    return line || t('ai.composerPlaceholder');
}
/**
 * DOES A MESSAGE IN THIS THREAD GO TO ITS AGENT, rather than start a turn of this tab's own?
 *
 * Yes while the agent the thread belongs to is unfinished — working, waiting, or not yet started —
 * because the server hands that run every new message in its thread. A turn of this tab's own on
 * top of it was a second answerer that never saw the task. A finished or stopped agent reads
 * nothing more, so there the person's message is a turn like any other.
 */
export function messageSteers(agentState) {
    return agentState !== undefined && agentState !== 'done' && agentState !== 'stopped';
}
/**
 * IS THIS AN AGENT'S THREAD THAT TAKES NO MORE MESSAGES? — a finished or stopped agent's.
 *
 * A message there started a turn of this tab's own under the agent's name, and that turn was not
 * the agent: no task, none of its tool results, and nothing it said counted as the agent's return.
 * The chat that started the agent is where a person asks about it; it can read the agent's notes
 * or wake it. A person's own thread is never closed.
 */
export function threadClosed(agentState) {
    return agentState !== undefined && !messageSteers(agentState);
}
/**
 * ONE ANSWER TO "WHAT DOES STOP STOP", for the button and for `/stop`.
 *
 * This tab's own turn first: it is the thing the person started here, and it is spending now. The
 * run second, and only while it is working — a parked agent is idle, and its composer is how the
 * person talks to it, so putting Stop where Send and Talk live told them the opposite (stopping it
 * is still offered from the header, where stopping always is). The turn is read as it is, not
 * through the run: a person's turn in a finished agent's thread was counted as idle because the
 * agent was, and had no Stop at all.
 */
export function composerAction(s) {
    if (s.turnBusy)
        return 'stop-turn';
    const working = messageSteers(s.agentState) && s.agentState !== 'awaiting';
    if (s.canStopRun && working && !s.hasDraft)
        return 'stop-run';
    return s.hasDraft ? 'send' : 'talk';
}
/**
 * WHETHER TAB TAKES THE SUGGESTED NEXT REPLY — asked after `DraftController.keydown`, so an IME or an
 * open list has already had the key.
 *
 * Only a bare Tab, only into an EMPTY box, and only while a line is offered. Everywhere else Tab is
 * what the browser makes it, moving focus, because a box that swallowed Tab whenever it had focus
 * would trap a keyboard user in it. It fills the box and does not send: the person sees, and can
 * change, what goes out in their name, and Enter sends it. Empty means empty, not blank: a box
 * holding only spaces no longer shows the grey line, so Tab has nothing visible to take.
 */
export function nextReplyTakesTab(e, draft, line) {
    if (e.key !== 'Tab' || e.shiftKey || e.altKey || e.ctrlKey || e.metaKey)
        return false;
    return !!line && draft === '';
}
/** The choices `/model`, `/effort` and `/specialist` offer: the same rows the pickers show. */
export function pickerOptions(specialists) {
    return {
        models: TEXT_MODELS.map((m) => ({ value: m.value, label: m.label, detail: m.provider })),
        efforts: EFFORTS.map((e) => ({ value: e, label: t(`ai.effort_${e}`) })),
        specialists: specialists.map((e) => ({ value: e.id, label: specialistLabel(e) })),
    };
}
export class DraftController {
    constructor(host, opts) {
        this.host = host;
        this.opts = opts;
        this._text = '';
        this._mentions = [];
        this._suggestions = [];
        this._active = 0;
        this._note = '';
        /** For the box's `input`. A keystroke clears the last command's note. */
        this.onInput = (e) => {
            const area = e.target;
            this._note = '';
            this.setText(area.value, { caret: area.selectionStart ?? area.value.length });
        };
        /**
         * For the box's `keyup` and `click`: the caret moved and the text did not — a click, an arrow,
         * Home or End.
         *
         * A mention query is the text BEFORE the caret, so moving the caret can open the list or close it.
         * Up and Down are left out: while the list is open they move through it.
         */
        this.onCaret = (e) => {
            const key = e.key;
            if (key !== undefined && !CARET_KEYS.has(key))
                return;
            const area = e.target;
            this.suggestAt(area.selectionStart ?? this._text.length);
        };
        /** For the box's `scroll`. The mark layer scrolls with the box, so a mark stays on its text in a
         *  long draft — which is why a view renders `renderBackdrop()` just before its textarea. */
        this.onScroll = (e) => {
            const area = e.target;
            const back = area.previousElementSibling;
            if (back)
                back.scrollTop = area.scrollTop;
        };
        /** Close the list — for the box's `blur`, and for Escape. */
        this.close = () => {
            this._suggestions = [];
            this.host.requestUpdate();
        };
        host.addController(this);
    }
    /** What is in the box. */
    get text() {
        return this._text;
    }
    /** The @ mentions in it — ranges of its text, and what each refers to. */
    get mentions() {
        return this._mentions;
    }
    /** What the list offers for the text. Empty means the list is closed. */
    get suggestions() {
        return this._suggestions;
    }
    get active() {
        return this._active;
    }
    /** What the last command did, or why it could not. Cleared by the next keystroke. */
    get note() {
        return this._note;
    }
    /**
     * The one setter — and the draft OUTLIVES the tab.
     *
     * Written through to local storage on every change rather than on unload: a closed tab never runs
     * cleanup, and "I closed it by accident" is exactly the case worth surviving. Everything that sets
     * the draft — typing, list continuation, a branch, a pick, a command, send — comes through here,
     * so none of them can forget to keep it. And the list and the mentions can never disagree with the
     * box: both are recomputed from the text on every change, and an empty box closes the list.
     *
     * The mentions are given outright when a caller placed or restored them, and moved with the edit
     * otherwise. An empty box holds none.
     */
    setText(text, opts = {}) {
        const prev = this._text;
        this._text = text;
        this._mentions = !text ? [] : (opts.mentions ?? rebaseMentions(prev, text, this._mentions));
        const slot = this.opts.slot();
        if (slot)
            saveDraft(slot.surface, slot.id, text, this._mentions);
        this.suggestAt(opts.caret ?? text.length);
    }
    /**
     * Refill the box with what was kept for the current slot. The list and the note go: they belong
     * to the draft they were typed in.
     *
     * Not through `setText`: restoring is not a change to keep, and writing it back would re-stamp the
     * draft's timestamp on every open — which would keep an abandoned sentence alive forever by the
     * act of looking at it.
     */
    restore() {
        const slot = this.opts.slot();
        this._text = slot ? readDraft(slot.surface, slot.id) : '';
        this._mentions = slot ? mentionsFor(this._text, readDraftMentions(slot.surface, slot.id)) : [];
        this._suggestions = [];
        this._active = 0;
        this._note = '';
        this.host.requestUpdate();
    }
    /** The draft as it is sent: each mention as its link, and the references it carries. */
    outgoing() {
        return serializeDraft(this._text, this._mentions);
    }
    /**
     * Run the text as a command, if it is one. True when it was handled as a command — run, refused,
     * or asked for its argument — and so must NOT be sent to the model.
     *
     * A slash and a name no command has is not a command: it returns false and the message is sent
     * as typed, so "/etc/hosts is missing" still reaches the model.
     */
    async runCommand(text) {
        const parsed = parseCommand(text);
        const cmd = parsed && findCommand(parsed.name);
        if (!parsed || !cmd)
            return false;
        const actions = this.actions;
        if (cmd.available && !cmd.available(actions)) {
            this.setNote(t('ai.cmdUnavailable', { name: cmd.name }));
            return true;
        }
        if (cmd.arg?.required && !parsed.arg) {
            // Ask for the argument in place: the box keeps the command and the list offers its options.
            this.setText(`/${cmd.name} `);
            this.setNote(t('ai.cmdNeedsArg', { name: cmd.name, arg: t(cmd.arg.hintKey) }));
            return true;
        }
        this.setText('');
        this.setNote('');
        await cmd.run(actions, parsed.arg);
        return true;
    }
    /**
     * A keydown in the box, before the view's own keys. True when it was taken here, and the view must
     * not act on it.
     *
     * A keystroke inside an IME composition belongs to the IME — Enter picks the word, Escape drops the
     * candidate (see `imeComposing`). While the list is open, its keys come first: the arrows move
     * through it, Enter picks a row, Tab completes the row's text without running it, and Escape
     * closes the list — and only the list, so one Escape never also abandons a branch.
     */
    keydown(e) {
        if (imeComposing(e))
            return true;
        const rows = this._suggestions.length;
        if (!rows)
            return false;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.setActive((this._active + (e.key === 'ArrowDown' ? 1 : rows - 1)) % rows);
            return true;
        }
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
            e.preventDefault();
            this.pick(this._active, e.key === 'Enter');
            return true;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            this.close();
            return true;
        }
        return false;
    }
    /** The list's ID while it is open, for the box's `aria-controls`. */
    get listId() {
        return this._suggestions.length ? LIST_ID : nothing;
    }
    /** The active row's ID while the list is open, for the box's `aria-activedescendant`. */
    get activeId() {
        return this._suggestions.length ? activeRowId({ id: LIST_ID, active: this._active }) : nothing;
    }
    /** The list. It opens above the box's nearest positioned wrapper. */
    renderList() {
        return renderSuggestList({
            id: LIST_ID,
            label: this._suggestions[0]?.mention ? t('ai.mentionListLabel') : t('ai.cmdListLabel'),
            items: this._suggestions,
            active: this._active,
            onPick: (i) => this.pick(i, true),
            onHover: (i) => this.setActive(i),
        });
    }
    renderNote() {
        return this._note ? html `<div class="cmd-note" role="status">${this._note}</div>` : nothing;
    }
    /** The layer that marks each mention — and draws `ghost`, the rest of what is being typed, after the
     *  last character (live-completion.ts). Rendered just before the textarea — see `onScroll`. */
    renderBackdrop(ghost = '') {
        return renderMentionBackdrop(this._text, this._mentions, ghost);
    }
    hostUpdated() {
        // The arrows can move past the rows the list shows; keep the active one in view.
        if (this._suggestions.length)
            this.host.renderRoot.querySelector('.suggest .opt.on')?.scrollIntoView({ block: 'nearest' });
    }
    /** What a command can do here: the view's actions, and the two that act on the box. */
    get actions() {
        return {
            ...this.opts.actions(),
            showCommands: () => this.setText('/'),
            note: (text) => this.setNote(text),
        };
    }
    /** The list for the text before the caret, from its first row. */
    suggestAt(caret) {
        this._suggestions = composerSuggestions(this._text, caret, this._mentions, this.actions);
        this._active = 0;
        this.host.requestUpdate();
    }
    setActive(index) {
        this._active = index;
        this.host.requestUpdate();
    }
    setNote(text) {
        this._note = text;
        this.host.requestUpdate();
    }
    /**
     * A row of the list was picked. A mention row places its entity. Otherwise `run` false — Tab —
     * only completes the text; Enter or a click runs a row that can run, and completes one that still
     * needs its argument.
     */
    pick(index, run) {
        const s = this._suggestions[index];
        if (!s)
            return;
        if (s.mention) {
            this.placeMention(s.mention);
            return;
        }
        if (run && s.runs) {
            void this.runCommand(s.insert);
            return;
        }
        this.setText(s.insert);
        this.focusAt(s.insert.length);
    }
    /** Put a picked entity where its query was typed, and leave the caret after it. */
    placeMention(item) {
        const r = applyMention(this._text, this.opts.area()?.selectionStart ?? this._text.length, this._mentions, item);
        if (!r)
            return;
        this.setText(r.text, { caret: r.caret, mentions: r.mentions });
        this.focusAt(r.caret);
    }
    /** Focus the box with the caret at `caret`, once the new text is rendered into it. */
    focusAt(caret) {
        void this.host.updateComplete.then(() => {
            const area = this.opts.area();
            area?.focus();
            area?.setSelectionRange(caret, caret);
        });
    }
}
/**
 * The list, the note and the mark layer, as both views draw them.
 *
 * Each view adds, after these, what must match its own textarea: the mark layer's padding, border,
 * line height and scrollbar width. A few pixels of difference are enough to wrap a word on one layer
 * and not on the other, and then a mark drifts off its text.
 */
export const draftStyles = [
    suggestStyles,
    css `
    .cmd-note {
      font-size: 12px;
      color: var(--text-secondary);
    }
    /* The box and the layer that marks its mentions, stacked: the same text, font, padding and
       wrapping, so each mark sits behind its own letters. */
    .field {
      position: relative;
    }
    .mention-backdrop {
      position: absolute;
      inset: 0;
      box-sizing: border-box;
      font: inherit;
      font-size: 14px;
      white-space: pre-wrap;
      overflow-wrap: break-word;
      overflow: hidden;
      /* Reserved whether or not the box scrolls, the same as on the textarea: both wrap at the same
         width, so a mark cannot drift off its text when the scrollbar appears. */
      scrollbar-gutter: stable;
      color: transparent;
      pointer-events: none;
    }
    .mention-backdrop mark.mention {
      border-radius: 3px;
      background: color-mix(in srgb, var(--ai) 26%, transparent);
      color: transparent;
    }
    /* The rest of what is being typed: the one part of this layer that is meant to be read. */
    .mention-backdrop .ghost {
      color: var(--text-tertiary);
    }
  `,
];
//# sourceMappingURL=draft-controller.js.map